import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { Header, Shimmers, TableComponent } from "components";
import { FC, useEffect, useMemo } from "react";
import { useMatch, useNavigate, useSearch } from "react-location";
import { LocationGenerics } from "router/location";
import {
  wrapClick,
  useUrlState,
  withRoles,
  useTableData,
  useDownloadFile,
} from "utils";
import config from "config";
import { CloudArrowDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import CreateContractorUserContainer from "./create";
import UpdateUserContainer from "./update";
import ViewUserContainer from "./view";
import { Avatar } from "components/core";
import ActionButton, { Action } from "components/buttons/action-button";
import DisableContractorUserContainer from "./disable";
import EnableContractorUserContainer from "./enable";
import ResetContractorUserFingerprintContainer from "./reset-fingerprint";
import toast from "react-hot-toast";

const GET_CONTRACTOR_USERS = gql`
  query GetContractorUsers(
    $page: Int
    $pageSize: Int
    $search: String
    $searchFields: [String!]
    $sort: String
    $contractor: ID
  ) {
    rows: getContractorUsers(
      page: $page
      pageSize: $pageSize
      search: $search
      searchFields: $searchFields
      sort: $sort
      contractor: $contractor
    ) {
      _id
      code
      username
      lastName
      firstName
      gender
      ghanaCardNumber
      phoneNumber
      emailAddress
      profileImageUrl
      createdAt
      meta {
        isBlocked
      }
      updatedAt
    }
    count: getContractorUsersCount(
      search: $search
      searchFields: $searchFields
      contractor: $contractor
    )
  }
`;

const GET_CONTRACTOR_USERS_EXPORT_URL = gql`
  query GetContractorUsersExportUrl(
    $search: String
    $searchFields: [String!]
    $sort: String
    $contractor: ID
  ) {
    url: getContractorUsersExportUrl(
      search: $search
      searchFields: $searchFields
      sort: $sort
      contractor: $contractor
    )
  }
`;

const ContractorUsersPage: FC = () => {
  const searchParams = useSearch<LocationGenerics>();
  const { params } = useMatch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const [modal, setModal] = useUrlState("modal");
  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  const filter = useMemo(
    () => ({
      page: searchParams.page || 1,
      pageSize: searchParams.pageSize || 12,
      search: (searchParams.search || "").toString(),
      contractor: params.contractor,
      searchFields: ["code", "lastName", "firstName", "ghanaCardNumber", "phoneNumber"],
      sort: searchParams.sort || "",
    }),
    [searchParams, params]
  );

  const { data, loading, networkStatus, refetch } = useQuery(
    GET_CONTRACTOR_USERS,
    {
      variables: filter,
      notifyOnNetworkStatusChange: false,
    }
  );
  const { downloadAction, downloadLoading } = useDownloadFile({
    onError: () => {
      toast(
        JSON.stringify({
          type: "error",
          title: "An error occurred downloading file",
        })
      );
    },
    getFileName: () => new Date().toISOString() + "_HasleFree.xlsx",
  });
  const handleDownloadItem = () => {
    createDataExport({
      variables: filter,
      fetchPolicy: "no-cache",
    }).then(({ data }) => {
      if (data.url) {
        downloadAction(`${data.url}`);
      } else {
        toast(
          JSON.stringify({ type: "error", title: "Could not export file" })
        );
      }
    });
  };

  const [createDataExport, { loading: gettingUrl }] = useLazyQuery(
    GET_CONTRACTOR_USERS_EXPORT_URL,
    {
      fetchPolicy: "no-cache",
    }
  );

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        ...(!old?.page ? { page: config.constants.page } : {}),
        ...(!old?.pageSize ? { pageSize: config.constants.pageSize } : {}),
        ...(!old?.search ? { search: "" } : {}),
        ...(!old?.sort ? { sort: "-createdAt" } : {}),
      }),
    });
  }, [navigate]);

  const records = useTableData(data || {});

  return (
    <main className='flex-1 flex flex-col overflow-hidden h-screen bg-gray-50'>
      <Header
        renderActions={() => (
          <>
            {withRoles(["Super"])(
              <button
                type='button'
                onClick={wrapClick(() => setModal("create"))}
                className='ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm leading-4 text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
              >
                <PlusIcon className='-ml-0.5 mr-2 h-4 w-4' aria-hidden='true' />
                Add New Agent
              </button>
            )}
          </>
        )}
      />
      <div className='flex flex-1 overflow-y-auto'>
        <div className='flex-1 min-h-full mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex'>
          <TableComponent
            title={"users"}
            refetch={refetch}
            isRefetching={loading && networkStatus === 4}
            loading={loading && ![4, 6].includes(networkStatus)}
            data={records}
            hasSearch={true}
            // renderExport={({ exportOpen, setExportOpen }) => (
            //   <ExportDataContainer
            //     open={exportOpen}
            //     setOpen={setExportOpen}
            //     entityType="User"
            //     filter={filter}
            //   />
            // )}
            renderHeaderItems={()=>(
              <div className="mt-4 sm:mt-0.5 sm:ml-16 sm:flex space-x-2">
                <button
                  type="button"
                  onClick={wrapClick(handleDownloadItem)}
                  disabled={gettingUrl || downloadLoading}
                  className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm leading-4 text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  <CloudArrowDownIcon
                    className="-ml-0.5 mr-2 h-4 w-4"
                    aria-hidden="true"
                  />
                  {gettingUrl
                    ? "Processing file..."
                    : downloadLoading
                    ? "Exporting..."
                    : "Export To Excel"}
                </button>
              </div>
            )}
            renderColumns={() => (
              <tr>
                <th
                  scope='col'
                  className='sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap'
                >
                  Agent Code
                </th>
                <th
                  scope='col'
                  className='sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap'
                >
                  AMC Username
                </th>
                <th
                  scope='col'
                  className='sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap'
                >
                  Name
                </th>
                <th
                  scope='col'
                  className='sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap'
                >
                  Phone Number
                </th>
                <th
                  scope='col'
                  className='sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap'
                >
                  Email Address
                </th>
                <th
                  scope='col'
                  className='sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap'
                >
                  Ghana Card
                </th>
                <th
                  scope='col'
                  className='sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap'
                ></th>
              </tr>
            )}
            renderLoader={() => (
              <tr>
                <td className='px-6 py-4 border-b border-gray-200'>
                  <Shimmers.AvatarShimmer />
                </td>
                <td className='px-6 py-4 border-b border-gray-200'>
                  <Shimmers.SingleShimmer />
                </td>
                <td className='px-6 py-4 border-b border-gray-200'>
                  <Shimmers.SingleShimmer />
                </td>
                <td className='px-6 py-4 border-b border-gray-200'>
                  <Shimmers.SingleShimmer />
                </td>
                <td className='px-6 py-4 border-b border-gray-200'>
                  <Shimmers.SingleShimmer />
                </td>
                <td className='px-6 py-4 border-b border-gray-200'>
                  <Shimmers.SingleShimmer />
                </td>
                <td className='px-6 py-4 border-b border-gray-200'>
                  <Shimmers.ActionsShimmer actionsCount={2} />
                </td>
              </tr>
            )}
            renderItem={(item) => (
              <tr
                key={item._id}
                className='hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer'
                onClick={wrapClick(dispatchAction(item._id, "view"))}
              >
                <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200'>
                  <div className='text-gray-900 dark:text-gray-100'>
                    {item?.code || "N/A"}
                  </div>
                </td>
                <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200'>
                  <div className='text-gray-500 dark:text-gray-400'>
                    {item?.username || "N/A"}
                  </div>
                </td>
                <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 '>
                  <div className='flex items-center'>
                    <div className='h-10 w-10 flex-shrink-0'>
                      <Avatar
                        disabled={true}
                        alt={
                          [
                            (item?.lastName || "")?.trim(),
                            (item?.firstName || "")?.trim(),
                          ]
                            .join(" ")
                            .trim() || "N A"
                        }
                        src={item?.profileImageUrl || ""}
                      />
                    </div>
                    <div className='ml-4'>
                      <div className='text-gray-900 dark:text-gray-100'>
                        {[
                          (item?.lastName || "")?.trim(),
                          (item?.firstName || "")?.trim(),
                        ]
                          .join(" ")
                          .trim() || "N A"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200'>
                  <div className='text-gray-500 dark:text-gray-400'>
                    {item?.phoneNumber || "N/A"}
                  </div>
                </td>
                <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200'>
                  <div className='text-gray-500 dark:text-gray-400'>
                    {item?.emailAddress || "N/A"}
                  </div>
                </td>
                <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200'>
                  <div className='text-gray-500 dark:text-gray-400'>
                    {item?.ghanaCardNumber || "N/A"}
                  </div>
                </td>
                <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 text-right'>
                  <div className='space-x-1'>
                    <ActionButton
                      action='view'
                      onClick={dispatchAction(item?._id, "view")}
                    />
                    {withRoles(["Super"])(
                      <ActionButton
                        action='update'
                        onClick={dispatchAction(item?._id, "update")}
                      />
                    )}
                      <ActionButton
                        action='reset'
                        onClick={dispatchAction(item?._id, "reset")}
                      />
                    {withRoles(["Super"])(
                      item?.meta?.isBlocked ? (
                        <ActionButton
                          action='enable'
                          onClick={dispatchAction(item?._id, "enable")}
                        />
                      ) : (
                        <ActionButton
                          action='disable'
                          onClick={dispatchAction(item?._id, "disable")}
                        />
                      )
                    )}
                  </div>
                </td>
              </tr>
            )}
          />
        </div>
      </div>

      {params.contractor && (
        <>
          {withRoles(["Super"])(
            <CreateContractorUserContainer
              open={modal === "create"}
              contractor={params.contractor}
              setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
              refetch={refetch}
            />
          )}
          {!!searchParams.id?.length && (
            <>
              {withRoles(["Super"])(
                <UpdateUserContainer
                  open={modal === "update"}
                  contractor={params.contractor}
                  setOpen={(val: boolean) =>
                    setModal(val ? "update" : undefined)
                  }
                  refetch={refetch}
                />
              )}
              <ViewUserContainer
                open={modal === "view"}
                refetch={refetch}
                setOpen={(val: boolean) => setModal(val ? "view" : undefined)}
              />
              <ResetContractorUserFingerprintContainer
                open={modal === "reset"}
                refetch={refetch}
                setOpen={(val: boolean) => setModal(val ? "reset" : undefined)}
              />
              {withRoles(["Super"])(
                <DisableContractorUserContainer
                  open={modal === "disable"}
                  setOpen={(val: boolean) =>
                    setModal(val ? "disable" : undefined)
                  }
                  refetch={refetch}
                />
              )}
              {withRoles(["Super"])(
                <EnableContractorUserContainer
                  open={modal === "enable"}
                  setOpen={(val: boolean) =>
                    setModal(val ? "enable" : undefined)
                  }
                  refetch={refetch}
                />
              )}
            </>
          )}
        </>
      )}
    </main>
  );
};

export default ContractorUsersPage;
